import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { PLATFORMS } from "../lib/format";
import { toast } from "sonner";
import { Link, Loader2, RefreshCw, Trash2, Clock, CheckCircle2, AlertCircle } from "lucide-react";

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "baru saja";
  if (diff < 3600) return `${Math.floor(diff / 60)} menit lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
  return `${Math.floor(diff / 86400)} hari lalu`;
};

export default function IcalImportDialog({ open, onOpenChange, onImported }) {
  const [rooms, setRooms] = useState([]);
  const [subs, setSubs] = useState([]);
  const [url, setUrl] = useState("");
  const [platform, setPlatform] = useState("Booking.com");
  const [roomName, setRoomName] = useState("");
  const [autoPrice, setAutoPrice] = useState(true);
  const [loading, setLoading] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);

  const loadSubs = async () => {
    try {
      const r = await api.get(`/ical/subscriptions`);
      setSubs(r.data || []);
    } catch {
      setSubs([]);
    }
  };

  useEffect(() => {
    if (open) {
      api.get(`/rooms`).then((r) => {
        setRooms(r.data);
        if (!roomName && r.data[0]) setRoomName(r.data[0].name);
      });
      loadSubs();
    }
    // eslint-disable-next-line
  }, [open]);

  const doImport = async () => {
    if (!url.trim() || !roomName) {
      toast.error("Lengkapi URL iCal dan pilih kamar.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/ical/import`, { url: url.trim(), room_name: roomName, platform, auto_price: autoPrice });
      const { imported, updated, skipped, conflicts_skipped } = res.data;
      toast.success(`Berhasil: ${imported} baru, ${updated} update, ${conflicts_skipped} skip (konflik), ${skipped} diabaikan.`);
      toast.message("URL tersimpan — auto-sync setiap 1 jam aktif.");
      setUrl("");
      onImported?.();
      loadSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal import iCal.");
    } finally {
      setLoading(false);
    }
  };

  const doSyncAll = async () => {
    if (subs.length === 0) {
      toast.error("Belum ada langganan kalender tersimpan.");
      return;
    }
    setSyncingAll(true);
    try {
      const r = await api.post(`/ical/sync-all`);
      const { totals, subscriptions_synced } = r.data;
      toast.success(`Sync ${subscriptions_synced} kalender: ${totals.imported} baru, ${totals.updated} update.`);
      onImported?.();
      loadSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync gagal.");
    } finally {
      setSyncingAll(false);
    }
  };

  const doDeleteSub = async (id, label) => {
    if (!confirm(`Hapus langganan ${label}? Booking yang sudah ter-import tidak ikut terhapus.`)) return;
    try {
      await api.delete(`/ical/subscriptions/${id}`);
      toast.success("Langganan dihapus.");
      loadSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal hapus langganan.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="ical-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Link className="h-5 w-5 text-[#D16B4D]" />
            Import & Sync iCal
          </DialogTitle>
          <DialogDescription className="text-[#586D61]">
            Sinkronisasi otomatis dari Booking.com, Airbnb, atau platform lain. Auto-sync berjalan tiap 1 jam.
          </DialogDescription>
        </DialogHeader>

        {/* Saved subscriptions */}
        {subs.length > 0 && (
          <div className="border border-[#EBE6DE] rounded-xl p-4 bg-[#FDFBF7]/40" data-testid="ical-subscriptions-list">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#586D61]">Langganan Kalender Aktif</div>
                <div className="text-xs text-[#586D61] mt-0.5">Auto-sync setiap 1 jam · {subs.length} kalender</div>
              </div>
              <Button
                data-testid="btn-sync-all-ical"
                onClick={doSyncAll}
                disabled={syncingAll}
                size="sm"
                className="bg-[#2C7A51] hover:bg-[#226641] text-white rounded-xl gap-2"
              >
                {syncingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Sync Sekarang
              </Button>
            </div>
            <div className="space-y-2">
              {subs.map((s) => {
                const hasError = !!s.last_error;
                const lr = s.last_result || {};
                return (
                  <div key={s.id} className="flex items-center justify-between gap-3 bg-white border border-[#EBE6DE] rounded-lg p-3" data-testid={`ical-sub-${s.id}`}>
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {hasError ? (
                        <AlertCircle className="h-4 w-4 text-[#D14D4D] mt-0.5 shrink-0" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 text-[#2C7A51] mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-[#172E21] truncate">
                          {s.platform} → {s.room_name}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-[#586D61] mt-0.5 flex-wrap">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {fmtRelative(s.last_synced_at)}
                          </span>
                          {!hasError && (
                            <span className="font-mono">
                              · {lr.imported || 0} baru / {lr.updated || 0} update
                            </span>
                          )}
                          {hasError && (
                            <span className="text-[#D14D4D] truncate">· {s.last_error}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      data-testid={`btn-delete-sub-${s.id}`}
                      onClick={() => doDeleteSub(s.id, `${s.platform} → ${s.room_name}`)}
                      variant="ghost"
                      size="sm"
                      className="text-[#D14D4D] hover:bg-[#D14D4D]/10 h-8 w-8 p-0"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add new subscription */}
        <div className="border border-[#EBE6DE] rounded-xl p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D16B4D] mb-3">
            {subs.length > 0 ? "Tambah Kalender Baru" : "Hubungkan Kalender Pertama"}
          </div>
          <div className="flex flex-col gap-3">
            <div>
              <Label className="text-sm font-medium">URL iCal</Label>
              <div className="text-[11px] text-[#586D61] mt-0.5">Ambil dari Booking.com extranet → Calendar → Sync calendars → Export iCal.</div>
              <Input
                data-testid="input-ical-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://admin.booking.com/hotel/hoteladmin/ical.html?..."
                className="mt-1.5 border-[#EBE6DE] font-mono text-xs"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Platform</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger data-testid="select-ical-platform" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Kamar Tujuan</Label>
                <Select value={roomName} onValueChange={setRoomName}>
                  <SelectTrigger data-testid="select-ical-room" className="mt-1.5"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-[#EBE6DE] bg-[#FDFBF7]/40 hover:border-[#D16B4D]/40 transition-colors" data-testid="label-auto-price">
              <Checkbox
                data-testid="checkbox-auto-price"
                checked={autoPrice}
                onCheckedChange={(v) => setAutoPrice(v === true)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-[#172E21]">Hitung otomatis harga (pakai tarif PondokRaja)</div>
                <div className="text-xs text-[#586D61] mt-0.5 leading-relaxed">
                  Booking ber-summary "CLOSED – Not available" akan di-rename jadi "<span className="font-medium">{platform} Guest</span>" dan diberi harga = <span className="font-mono">base_rate × musim × malam</span>.
                </div>
              </div>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Tutup</Button>
          <Button
            data-testid="btn-run-ical-import"
            onClick={doImport}
            disabled={loading}
            className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
          >
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</> : "Tambah & Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
